import gettext from '@sdk/lang/gettext';
import Row from '@sdk/components/ui/spacing/Row';
import H3 from '@sdk/components/ui/typography/H3';
import Text14 from '@sdk/components/ui/typography/Text14';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { SUBSCRIPTION_NAMES } from '@sdk/common/constants';
import moneyDisplay from '@sdk/common/currency/moneyDisplay';
import dateToMonthDay from '@sdk/utils/dateFormat/dateToMonthDay';
import getFourDigitYear from '@sdk/utils/dateFormat/getFourDigitYear';
import Tooltip from '@sdk/common/tooltip/Tooltip';
import { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import { BillingInfoType } from '@sdk/account/api/AccountApi';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';

import { usePlanDetails } from '@web/account/purchase/PlanDetails/planDetails.hooks';
import { PlanDetailsRow } from '@web/account/purchase/PlanDetailsRow/PlanDetailsRow';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import { ANNUAL_DISCOUNT_PERCENTAGE } from '@web/account/constants';

import PurchaseColumn from '../PurchaseColumn';

import { Plan, InfoIcon, DiscountInfo } from './planDetails.styles';

type Props = {
  yearlyPlanDiscount: number;
  monthlyPricePerUserPerMonth: number;
  currency: CurrencyType;
  priceSummary: {
    discount: number;
    taxAmount: number;
    subtotalPrice: number;
    totalNetPrice: number;
    totalGrossPrice: number;
  };
  billedOnDate: Date;
  areYearlySubscriptionsEnabled: boolean;
  isShopifyBilling: boolean;
  postSubscriptionPurchase: (subscription: BillingInfoType) => Promise<void>;
  currentUser: AuthUserType;
};

const PlanDetails = ({
  yearlyPlanDiscount,
  monthlyPricePerUserPerMonth,
  currency,
  priceSummary,
  billedOnDate,
  areYearlySubscriptionsEnabled,
  isShopifyBilling,
  postSubscriptionPurchase,
  currentUser,
}: Props) => {
  const { billingInfo } = useBillingInfo();
  const { completePurchase, subscriptionLevel, isYearlyPlan } = usePlanDetails({
    isShopifyBilling,
    currentUser,
    postSubscriptionPurchase,
  });

  return (
    <Plan mTop={isShopifyBilling ? -60 : 0}>
      <PurchaseColumn isSingleColumn gap={10}>
        <H3 themeColor={ThemeColorType.primaryText}>{gettext('Your plan')}</H3>
        <Row alignItems="center">
          <Text14 themeColor={ThemeColorType.gray}>
            {SUBSCRIPTION_NAMES[subscriptionLevel]}
            {' / '}
            {isYearlyPlan ? gettext('Annual') : gettext('Monthly')}
          </Text14>
          {(areYearlySubscriptionsEnabled && !isYearlyPlan && yearlyPlanDiscount && (
            <DiscountInfo themeColor={ThemeColorType.success}>
              {gettext('Save')} [{moneyDisplay({ value: yearlyPlanDiscount, currency })}]{' '}
              {gettext('per year with an annual plan')}
            </DiscountInfo>
          )) ||
            null}
        </Row>

        <HorizontalSeparator mBottom={10} mTop={10} />

        <PlanDetailsRow
          main={
            <Row alignItems="center">
              <Text14 themeColor={ThemeColorType.gray}>{gettext('Number of users')}</Text14>
              <InfoIcon
                data-tip={gettext(
                  'Sling automatically adjusts your invoice as the number of users on your account changes'
                )}
                data-place={'bottom'}
              />
              <Tooltip />
            </Row>
          }
          side={billingInfo.quantity}
        />

        <PlanDetailsRow
          main={gettext('Price per user')}
          side={moneyDisplay({ value: monthlyPricePerUserPerMonth, currency })}
        />

        {isYearlyPlan && <PlanDetailsRow main={gettext('Annual billing')} side="x 12" />}
        {Boolean(
          priceSummary.discount || (isYearlyPlan && yearlyPlanDiscount) || priceSummary.taxAmount
        ) && (
          <>
            <PlanDetailsRow
              main={gettext('Subtotal')}
              side={moneyDisplay({ value: priceSummary.subtotalPrice, currency })}
            />
            {Boolean(isYearlyPlan && yearlyPlanDiscount) && (
              <PlanDetailsRow
                main={
                  <>
                    {gettext('Annual plan discount ')}(
                    <Text14 themeColor={ThemeColorType.success}>
                      {ANNUAL_DISCOUNT_PERCENTAGE}%
                    </Text14>
                    )
                  </>
                }
                side={moneyDisplay({ value: -yearlyPlanDiscount, currency })}
              />
            )}
            {Boolean(priceSummary.discount) && (
              <PlanDetailsRow
                main={gettext('Coupon')}
                side={moneyDisplay({ value: priceSummary.discount, currency })}
              />
            )}
            {Boolean(priceSummary.taxAmount) && (
              <PlanDetailsRow
                main={gettext('Tax')}
                side={moneyDisplay({ value: priceSummary.taxAmount, currency })}
              />
            )}
          </>
        )}

        <HorizontalSeparator mTop={10} />

        <PlanDetailsRow
          main={gettext('Total')}
          side={moneyDisplay({ value: priceSummary.totalGrossPrice, currency })}
        />

        <PlanDetailsRow
          main={gettext('Billed on')}
          side={
            <>
              {dateToMonthDay(billedOnDate)}, {getFourDigitYear(billedOnDate)}
            </>
          }
        />

        <Row mTop={10}>
          <ProgressButton buttonType={ButtonType.primary} onClick={completePurchase}>
            {gettext('Complete purchase')}
          </ProgressButton>
        </Row>
      </PurchaseColumn>
    </Plan>
  );
};

export default PlanDetails;
